import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  KeyRound,
  Pencil,
  Plus,
  Search,
  Shield,
  ShieldOff,
  UserCircle2,
  UserPlus,
  X,
} from "lucide-react";
import { api } from "@/lib/api";
import { useEsAdmin } from "@/hooks/useEsAdmin";

interface UsuarioListItem {
  id: string;
  keycloakSubject: string | null;
  correo: string;
  nombre: string;
  apellidoPaterno: string;
  apellidoMaterno: string | null;
  tipo: string;
  estatus: string;
  rolesKeycloak: string[];
  fechaCreacion: string;
  fechaActualizacion: string;
}

interface PageResp<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
}

const PAGE_SIZE = 20;

const ROLES_INTERNOS = [
  "ADMIN_SICEN",
  "GERENTE_CALIDAD",
  "AUXILIAR_CALIDAD",
  "COORDINADOR_TECNICO",
  "EVALUADOR_TECNICO",
  "RESPONSABLE_TELECOM",
  "RESPONSABLE_ELECTRICA",
  "RESPONSABLE_EFICIENCIA",
  "AUDITOR_INTERNO",
  "DIRECCION",
  "FACTURACION",
] as const;

/** Roles de usuario externo. Una cuenta es de NPMX o de una empresa, nunca de las dos. */
const ROLES_EXTERNOS = [
  "USUARIO_GESTOR",
  "REPRESENTANTE_LEGAL",
  "TRAMITADOR",
  "TITULAR",
] as const;

const ROLES_INTERNOS_SET: ReadonlySet<string> = new Set(ROLES_INTERNOS);

const TIPO_OPCIONES = [
  { value: "INTERNO", label: "Internos (personal NPMX)" },
  { value: "GESTOR", label: "Externos — gestores" },
  { value: "REPRESENTANTE", label: "Externos — representantes" },
  { value: "TRAMITADOR", label: "Externos — tramitadores" },
  { value: "TITULAR", label: "Externos — titulares" },
];

const ESTATUS_OPCIONES = [
  { value: "", label: "Todos los estatus" },
  { value: "ACTIVO", label: "Activos" },
  { value: "SUSPENDIDO", label: "Suspendidos" },
  { value: "BAJA", label: "Baja" },
  { value: "PENDIENTE_ACTIVACION", label: "Pendiente activación" },
];

export function UsuariosInternosListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const appliedQ = searchParams.get("q") ?? "";
  const estatus = searchParams.get("estatus") ?? "";
  const rol = searchParams.get("rol") ?? "";
  const tipo = searchParams.get("tipo") ?? "INTERNO";
  const page = Math.max(0, Number.parseInt(searchParams.get("page") ?? "0", 10) || 0);
  const esAdmin = useEsAdmin();

  const [qInput, setQInput] = useState(appliedQ);
  useEffect(() => setQInput(appliedQ), [appliedQ]);

  const [accion, setAccion] = useState<
    | { tipo: "estatus"; usuario: UsuarioListItem }
    | { tipo: "rol"; usuario: UsuarioListItem }
    | { tipo: "datos"; usuario: UsuarioListItem }
    | { tipo: "reset"; usuario: UsuarioListItem }
    | { tipo: "aprovisionar"; usuario: UsuarioListItem }
    | null
  >(null);

  const query = useQuery({
    queryKey: ["admin-usuarios", appliedQ, estatus, rol, tipo, page],
    queryFn: async () => {
      const params = new URLSearchParams({ size: String(PAGE_SIZE), page: String(page), sort: "correo", tipo });
      if (appliedQ) params.set("q", appliedQ);
      if (estatus) params.set("estatus", estatus);
      if (rol) params.set("rol", rol);
      const { data } = await api.get<PageResp<UsuarioListItem>>(`/api/iam/usuarios?${params.toString()}`);
      return data;
    },
  });

  const totalElements = query.data?.totalElements ?? 0;
  const totalPages = query.data?.totalPages ?? 0;
  const hayFiltros = !!appliedQ || !!estatus || !!rol;

  function updateParam(updates: Record<string, string | null>, opts: { resetPage?: boolean } = { resetPage: true }) {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(updates)) {
      if (v == null || v === "") next.delete(k);
      else next.set(k, v);
    }
    if (opts.resetPage) next.delete("page");
    setSearchParams(next, { replace: false });
  }

  function limpiar() {
    setSearchParams(new URLSearchParams(), { replace: false });
    setQInput("");
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-fg">Usuarios</h1>
          <p className="text-sm text-fg-muted">
            Altas, estatus y reset de password de cuentas del sistema (personal NPMX y externos).
            Las cuentas de administrador solo las gestiona otro administrador.
          </p>
        </div>
        <Link
          to="/admin/usuarios/nuevo"
          className="inline-flex items-center gap-2 rounded bg-primary px-3 py-2 text-sm font-medium text-white transition hover:bg-primary-hover"
        >
          <Plus className="h-4 w-4" /> Nuevo usuario
        </Link>
      </header>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          updateParam({ q: qInput.trim() || null });
        }}
        className="flex flex-wrap items-center gap-2 rounded-lg border bg-surface p-3"
      >
        <div className="flex flex-1 min-w-[260px] items-center gap-2 rounded border border-edge-strong bg-surface px-2">
          <Search className="h-4 w-4 text-fg-faint" />
          <input
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Correo, nombre o apellido…"
            className="flex-1 border-0 bg-transparent px-1 py-1.5 text-sm focus:outline-none"
          />
        </div>
        <select
          value={tipo}
          onChange={(e) => updateParam({ tipo: e.target.value === "INTERNO" ? null : e.target.value, rol: null })}
          className="rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          title="Tipo de usuario"
        >
          {TIPO_OPCIONES.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {tipo === "INTERNO" && (
          <select
            value={rol}
            onChange={(e) => updateParam({ rol: e.target.value || null })}
            className="rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            title="Rol"
          >
            <option value="">Todos los roles</option>
            {ROLES_INTERNOS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        )}
        <select
          value={estatus}
          onChange={(e) => updateParam({ estatus: e.target.value || null })}
          className="rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          title="Estatus"
        >
          {ESTATUS_OPCIONES.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <button type="submit" className="cursor-pointer rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover">
          Buscar
        </button>
        {hayFiltros && (
          <button
            type="button"
            onClick={limpiar}
            className="inline-flex cursor-pointer items-center gap-1 rounded border px-2 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2"
          >
            <X className="h-3.5 w-3.5" /> Limpiar
          </button>
        )}
      </form>

      <div className="rounded-lg border bg-surface shadow-card">
        {query.isLoading && <div className="p-6 text-sm text-fg-subtle">Cargando…</div>}
        {query.error && (
          <div className="flex items-start gap-2 p-4 text-sm text-estado-warn">
            <AlertCircle className="mt-0.5 h-4 w-4" />
            Error cargando usuarios.
          </div>
        )}
        {query.data && (
          <>
            <div className="flex items-center justify-between border-b px-4 py-2 text-xs uppercase tracking-wide text-fg-subtle">
              <span>{totalElements} usuarios</span>
              {totalPages > 1 && (
                <span>
                  Página {page + 1} de {totalPages}
                </span>
              )}
            </div>
            {query.data.content.length === 0 ? (
              <div className="flex items-center gap-3 p-6 text-sm text-fg-subtle">
                <UserCircle2 className="h-5 w-5" />
                No se encontraron usuarios con los filtros actuales.
              </div>
            ) : (
              <div className="w-full overflow-x-auto">
              <table className="min-w-full divide-y text-sm">
                <thead className="bg-surface-2">
                  <tr>
                    {["Correo", "Nombre", "Rol", "Estatus", "Creado", "Acciones"].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-fg-subtle"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y bg-surface">
                  {query.data.content.map((u) => (
                    <tr key={u.id} className="transition hover:bg-surface-2">
                      <td className="px-4 py-2 font-mono text-xs">{u.correo}</td>
                      <td className="px-4 py-2">
                        {u.nombre} {u.apellidoPaterno}
                        {u.apellidoMaterno ? ` ${u.apellidoMaterno}` : ""}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        <div className="flex flex-wrap gap-1">
                          {u.rolesKeycloak.length === 0 && <span className="text-fg-faint">—</span>}
                          {u.rolesKeycloak.map((r) => (
                            <Badge key={r} tone="info">
                              {r}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <Badge tone={toneEstatus(u.estatus)}>{u.estatus}</Badge>
                      </td>
                      <td className="px-4 py-2 text-xs text-fg-subtle">{formatoFecha(u.fechaCreacion)}</td>
                      <td className="px-4 py-2">
                        {(() => {
                          // D-04/IAM: las cuentas admin solo las gestiona otro admin
                          // (mismo escudo en el backend; aquí solo evitamos el 403).
                          const objetivoBlindado = !esAdmin && u.rolesKeycloak.includes("ADMIN_SICEN");
                          const tituloBlindado =
                            "Las cuentas de administrador solo las gestiona otro administrador.";
                          return (
                        <div className="flex flex-wrap gap-1">
                          <button
                            type="button"
                            onClick={() => setAccion({ tipo: "estatus", usuario: u })}
                            disabled={objetivoBlindado}
                            className="inline-flex cursor-pointer items-center gap-1 rounded border px-2 py-1 text-xs transition hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50"
                            title={objetivoBlindado ? tituloBlindado : "Cambiar estatus (activar/suspender/baja)"}
                          >
                            {u.estatus === "ACTIVO" ? (
                              <ShieldOff className="h-3 w-3" />
                            ) : (
                              <Shield className="h-3 w-3" />
                            )}
                            Estatus
                          </button>
                          {!u.keycloakSubject ? (
                            esAdmin && (
                              <button
                                type="button"
                                onClick={() => setAccion({ tipo: "aprovisionar", usuario: u })}
                                className="inline-flex cursor-pointer items-center gap-1 rounded border border-estado-warn/25 bg-estado-warn-bg px-2 py-1 text-xs text-estado-warn transition hover:border-estado-warn/40"
                                title="Crear cuenta en Keycloak y asignar rol (el usuario no la tiene aún)"
                              >
                                <UserPlus className="h-3 w-3" />
                                Aprovisionar
                              </button>
                            )
                          ) : (
                            <>
                              {esAdmin && (
                                <button
                                  type="button"
                                  onClick={() => setAccion({ tipo: "rol", usuario: u })}
                                  className="inline-flex cursor-pointer items-center gap-1 rounded border px-2 py-1 text-xs transition hover:bg-surface-2"
                                  title="Cambiar rol asignado"
                                >
                                  <UserCircle2 className="h-3 w-3" />
                                  Rol
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => setAccion({ tipo: "datos", usuario: u })}
                                className="inline-flex cursor-pointer items-center gap-1 rounded border px-2 py-1 text-xs transition hover:bg-surface-2"
                                title="Corregir nombre y apellidos"
                              >
                                <Pencil className="h-3 w-3" />
                                Datos
                              </button>
                              <button
                                type="button"
                                onClick={() => setAccion({ tipo: "reset", usuario: u })}
                                disabled={objetivoBlindado}
                                className="inline-flex cursor-pointer items-center gap-1 rounded border px-2 py-1 text-xs transition hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50"
                                title={objetivoBlindado ? tituloBlindado : "Resetear password (genera una temporal)"}
                              >
                                <KeyRound className="h-3 w-3" />
                                Reset pwd
                              </button>
                            </>
                          )}
                        </div>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t bg-surface-2 px-4 py-2 text-sm">
                <button
                  type="button"
                  disabled={page <= 0}
                  onClick={() => updateParam({ page: String(page - 1) }, { resetPage: false })}
                  className="cursor-pointer rounded border bg-surface px-3 py-1 text-xs text-fg-muted transition hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  ← Anterior
                </button>
                <span className="text-xs text-fg-subtle">
                  Mostrando {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalElements)} de {totalElements}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages - 1}
                  onClick={() => updateParam({ page: String(page + 1) }, { resetPage: false })}
                  className="cursor-pointer rounded border bg-surface px-3 py-1 text-xs text-fg-muted transition hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Siguiente →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {accion?.tipo === "estatus" && (
        <ModalEstatus usuario={accion.usuario} onClose={() => setAccion(null)} />
      )}
      {accion?.tipo === "datos" && (
        <ModalDatos usuario={accion.usuario} onClose={() => setAccion(null)} />
      )}
      {accion?.tipo === "rol" && (
        <ModalRol usuario={accion.usuario} onClose={() => setAccion(null)} />
      )}
      {accion?.tipo === "reset" && (
        <ModalReset usuario={accion.usuario} onClose={() => setAccion(null)} />
      )}
      {accion?.tipo === "aprovisionar" && (
        <ModalAprovisionar usuario={accion.usuario} onClose={() => setAccion(null)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modales de acción
// ---------------------------------------------------------------------------

function ModalEstatus({ usuario, onClose }: { usuario: UsuarioListItem; onClose: () => void }) {
  const qc = useQueryClient();
  const [nuevoEstatus, setNuevoEstatus] = useState(usuario.estatus === "ACTIVO" ? "SUSPENDIDO" : "ACTIVO");
  const [motivo, setMotivo] = useState("");
  const mutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.put(`/api/iam/usuarios/${usuario.id}/estatus`, {
        nuevoEstatus,
        motivo,
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-usuarios"] });
      onClose();
    },
  });

  return (
    <ModalShell title={`Cambiar estatus de ${usuario.correo}`} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <p className="text-fg-muted">
          Estatus actual: <Badge tone={toneEstatus(usuario.estatus)}>{usuario.estatus}</Badge>
        </p>
        <label className="block">
          <span className="text-xs uppercase text-fg-subtle">Nuevo estatus</span>
          <select
            value={nuevoEstatus}
            onChange={(e) => setNuevoEstatus(e.target.value)}
            className="mt-1 w-full rounded border px-2 py-1.5"
          >
            <option value="ACTIVO">ACTIVO (habilitar)</option>
            <option value="SUSPENDIDO">SUSPENDIDO (bloquear temporal)</option>
            <option value="BAJA">BAJA (deshabilitar permanente)</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs uppercase text-fg-subtle">Motivo (queda en bitácora)</span>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
            className="mt-1 w-full rounded border px-2 py-1.5"
            placeholder="Ej. usuario egresado, suspensión preventiva, error en alta…"
          />
        </label>
        {mutation.error && (
          <p className="rounded border border-estado-err/25 bg-estado-err-bg p-2 text-xs text-estado-err">
            {errorMessage(mutation.error)}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="cursor-pointer rounded border px-3 py-1.5 text-sm transition hover:bg-surface-2">
            Cancelar
          </button>
          <button
            type="button"
            disabled={!motivo.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
            className="cursor-pointer rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover disabled:opacity-50"
          >
            {mutation.isPending ? "Guardando…" : "Confirmar"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

/**
 * Corrección de nombre y apellidos.
 *
 * Existe porque hasta ahora el módulo dejaba cambiar estatus, rol y contraseña
 * pero no arreglar un nombre mal capturado, y la única salida era borrar la
 * cuenta y volver a crearla.
 *
 * El correo se muestra en gris y no se edita: es el identificador con el que la
 * persona entra y con el que se resuelven sus relaciones.
 */
function ModalDatos({ usuario, onClose }: { usuario: UsuarioListItem; onClose: () => void }) {
  const qc = useQueryClient();
  const [nombre, setNombre] = useState(usuario.nombre ?? "");
  const [apellidoPaterno, setApellidoPaterno] = useState(usuario.apellidoPaterno ?? "");
  const [apellidoMaterno, setApellidoMaterno] = useState(usuario.apellidoMaterno ?? "");
  const [motivo, setMotivo] = useState("");

  const mutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.patch(`/api/iam/usuarios/${usuario.id}/datos`, {
        nombre,
        apellidoPaterno,
        apellidoMaterno,
        motivo,
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-usuarios"] });
      onClose();
    },
  });

  // Se comparan ya recortados: cambiar solo espacios no es un cambio.
  const hayCambio =
    nombre.trim() !== (usuario.nombre ?? "").trim() ||
    apellidoPaterno.trim() !== (usuario.apellidoPaterno ?? "").trim() ||
    apellidoMaterno.trim() !== (usuario.apellidoMaterno ?? "").trim();
  const completo = !!nombre.trim() && !!apellidoPaterno.trim();

  return (
    <ModalShell title={`Corregir datos de ${usuario.correo}`} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <p className="rounded border bg-surface-2 p-2 text-xs text-fg-muted">
          El correo <span className="font-mono">{usuario.correo}</span> no se modifica aquí: es el
          identificador con el que entra y con el que se resuelven sus relaciones. Tampoco cambian su
          rol, su tipo ni sus autorizaciones.
        </p>
        <label className="block">
          <span className="text-xs uppercase text-fg-subtle">Nombre</span>
          <input
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            className="mt-1 w-full rounded border px-2 py-1.5"
          />
        </label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="text-xs uppercase text-fg-subtle">Apellido paterno</span>
            <input
              value={apellidoPaterno}
              onChange={(e) => setApellidoPaterno(e.target.value)}
              className="mt-1 w-full rounded border px-2 py-1.5"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase text-fg-subtle">
              Apellido materno <span className="normal-case text-fg-faint">(opcional)</span>
            </span>
            <input
              value={apellidoMaterno}
              onChange={(e) => setApellidoMaterno(e.target.value)}
              className="mt-1 w-full rounded border px-2 py-1.5"
            />
          </label>
        </div>
        <label className="block">
          <span className="text-xs uppercase text-fg-subtle">Motivo</span>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={2}
            className="mt-1 w-full rounded border px-2 py-1.5"
            placeholder="Ej. corrección de nombre capturado incorrectamente durante el alta."
          />
        </label>
        {mutation.error && (
          <p className="rounded border border-estado-err/25 bg-estado-err-bg p-2 text-xs text-estado-err">
            {errorMessage(mutation.error)}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="cursor-pointer rounded border px-3 py-1.5 text-sm transition hover:bg-surface-2">
            Cancelar
          </button>
          <button
            type="button"
            disabled={!completo || !hayCambio || !motivo.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
            className="cursor-pointer rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover disabled:opacity-50"
          >
            {mutation.isPending ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function ModalRol({ usuario, onClose }: { usuario: UsuarioListItem; onClose: () => void }) {
  const qc = useQueryClient();
  const rolActual = usuario.rolesKeycloak[0] ?? "";
  const [nuevoRol, setNuevoRol] = useState<string>(rolActual);
  const [motivo, setMotivo] = useState("");
  // Cambiar de lado (NPMX ↔ externo) altera qué solicitudes ve la cuenta, así
  // que se avisa antes de confirmar y no después.
  const esRolInternoNuevo = ROLES_INTERNOS_SET.has(nuevoRol);
  const cambiaLado =
    !!nuevoRol && nuevoRol !== rolActual && esRolInternoNuevo !== (usuario.tipo === "INTERNO");
  const mutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.put(`/api/iam/usuarios/${usuario.id}/rol`, { nuevoRol, motivo });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-usuarios"] });
      onClose();
    },
  });

  return (
    <ModalShell title={`Cambiar rol de ${usuario.correo}`} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <p className="text-fg-muted">
          Roles actuales:{" "}
          {usuario.rolesKeycloak.length === 0 && <span className="text-fg-faint">sin rol asignado</span>}
          {usuario.rolesKeycloak.map((r) => (
            <Badge key={r} tone="info">
              {r}
            </Badge>
          ))}
        </p>
        <label className="block">
          <span className="text-xs uppercase text-fg-subtle">Nuevo rol</span>
          <select
            value={nuevoRol}
            onChange={(e) => setNuevoRol(e.target.value)}
            className="mt-1 w-full rounded border px-2 py-1.5"
          >
            <option value="">— elegir rol —</option>
            <optgroup label="Personal de NPMX">
              {ROLES_INTERNOS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </optgroup>
            <optgroup label="Usuario externo (cliente o gestoría)">
              {ROLES_EXTERNOS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </optgroup>
          </select>
        </label>
        {cambiaLado && (
          <p className="rounded border border-estado-warn/25 bg-estado-warn-bg p-2 text-xs text-estado-warn">
            {esRolInternoNuevo
              ? "Esta cuenta pasa a ser personal de NPMX: dejará de estar limitada a las empresas con las que tiene relación y verá las solicitudes de todos los titulares."
              : "Esta cuenta pasa a ser de usuario externo: se le retiran los roles de NPMX y solo verá las solicitudes de las empresas con las que tenga autorización vigente."}{" "}
            Se conservan el usuario, su histórico y sus relaciones.
          </p>
        )}
        <label className="block">
          <span className="text-xs uppercase text-fg-subtle">Motivo</span>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
            className="mt-1 w-full rounded border px-2 py-1.5"
            placeholder="Ej. promoción a gerente, reasignación de sector…"
          />
        </label>
        {mutation.error && (
          <p className="rounded border border-estado-err/25 bg-estado-err-bg p-2 text-xs text-estado-err">
            {errorMessage(mutation.error)}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="cursor-pointer rounded border px-3 py-1.5 text-sm transition hover:bg-surface-2">
            Cancelar
          </button>
          <button
            type="button"
            disabled={!nuevoRol || !motivo.trim() || nuevoRol === rolActual || mutation.isPending}
            onClick={() => mutation.mutate()}
            className="cursor-pointer rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover disabled:opacity-50"
          >
            {mutation.isPending ? "Guardando…" : "Confirmar"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function ModalReset({ usuario, onClose }: { usuario: UsuarioListItem; onClose: () => void }) {
  const [motivo, setMotivo] = useState("");
  const [pwd, setPwd] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);
  const mutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.put<{ passwordTemporal: string }>(
        `/api/iam/usuarios/${usuario.id}/reset-password`,
        { motivo },
      );
      return data;
    },
    onSuccess: (data) => setPwd(data.passwordTemporal),
  });

  async function copiar() {
    if (!pwd) return;
    await navigator.clipboard.writeText(pwd);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  return (
    <ModalShell title={`Resetear password de ${usuario.correo}`} onClose={onClose}>
      <div className="space-y-3 text-sm">
        {pwd ? (
          <>
            <div className="rounded border border-estado-warn/25 bg-estado-warn-bg p-3 text-estado-warn">
              <p className="font-medium">Password temporal generada</p>
              <p className="mt-1 text-xs">
                El usuario debe cambiarla obligatoriamente en su próximo login (UPDATE_PASSWORD). Cópiala ahora — no se
                volverá a mostrar.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded border bg-surface-2 px-3 py-2 font-mono text-sm">{pwd}</code>
              <button
                type="button"
                onClick={copiar}
                className="cursor-pointer rounded border bg-surface px-3 py-2 text-xs transition hover:bg-surface-2"
              >
                {copiado ? "Copiado ✓" : "Copiar"}
              </button>
            </div>
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={onClose}
                className="cursor-pointer rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover"
              >
                Cerrar
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-fg-muted">
              Se generará una password temporal fuerte y se asignará al usuario en Keycloak. La verás una sola vez
              después de confirmar.
            </p>
            <label className="block">
              <span className="text-xs uppercase text-fg-subtle">Motivo</span>
              <textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                rows={3}
                className="mt-1 w-full rounded border px-2 py-1.5"
                placeholder="Ej. usuario olvidó password, reset preventivo tras incidente…"
              />
            </label>
            {mutation.error && (
              <p className="rounded border border-estado-err/25 bg-estado-err-bg p-2 text-xs text-estado-err">
                {errorMessage(mutation.error)}
              </p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="cursor-pointer rounded border px-3 py-1.5 text-sm transition hover:bg-surface-2">
                Cancelar
              </button>
              <button
                type="button"
                disabled={!motivo.trim() || mutation.isPending}
                onClick={() => mutation.mutate()}
                className="cursor-pointer rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover disabled:opacity-50"
              >
                {mutation.isPending ? "Generando…" : "Resetear password"}
              </button>
            </div>
          </>
        )}
      </div>
    </ModalShell>
  );
}

function ModalAprovisionar({ usuario, onClose }: { usuario: UsuarioListItem; onClose: () => void }) {
  const qc = useQueryClient();
  const [rol, setRol] = useState("");
  const [motivo, setMotivo] = useState("");
  const [resultado, setResultado] = useState<{
    passwordTemporal: string | null;
    yaExistiaEnKeycloak: boolean;
  } | null>(null);
  const [copiado, setCopiado] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ passwordTemporal: string | null; yaExistiaEnKeycloak: boolean }>(
        `/api/iam/usuarios/${usuario.id}/aprovisionar-keycloak`,
        { rol, motivo },
      );
      return data;
    },
    onSuccess: (data) => {
      setResultado({ passwordTemporal: data.passwordTemporal, yaExistiaEnKeycloak: data.yaExistiaEnKeycloak });
      qc.invalidateQueries({ queryKey: ["admin-usuarios"] });
    },
  });

  async function copiar() {
    if (!resultado?.passwordTemporal) return;
    await navigator.clipboard.writeText(resultado.passwordTemporal);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  return (
    <ModalShell title={`Aprovisionar Keycloak: ${usuario.correo}`} onClose={onClose}>
      <div className="space-y-3 text-sm">
        {resultado ? (
          <>
            {resultado.yaExistiaEnKeycloak ? (
              <div className="rounded border border-estado-info/25 bg-estado-info-bg p-3 text-estado-info">
                <p className="font-medium">Cuenta Keycloak vinculada</p>
                <p className="mt-1 text-xs">
                  El correo ya existía en Keycloak (creado fuera de SICEN). Sólo vinculamos la cuenta a este usuario y
                  le asignamos el rol. La password actual del usuario no se tocó. Si la necesita resetear, usa el
                  botón Reset pwd que ahora estará disponible.
                </p>
              </div>
            ) : (
              <>
                <div className="rounded border border-estado-warn/25 bg-estado-warn-bg p-3 text-estado-warn">
                  <p className="font-medium">Cuenta creada en Keycloak</p>
                  <p className="mt-1 text-xs">
                    Generamos password temporal y asignamos el rol. El usuario deberá cambiarla obligatoriamente en su
                    primer login (UPDATE_PASSWORD). Cópiala — no se volverá a mostrar.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded border bg-surface-2 px-3 py-2 font-mono text-sm">
                    {resultado.passwordTemporal}
                  </code>
                  <button
                    type="button"
                    onClick={copiar}
                    className="cursor-pointer rounded border bg-surface px-3 py-2 text-xs transition hover:bg-surface-2"
                  >
                    {copiado ? "Copiado ✓" : "Copiar"}
                  </button>
                </div>
              </>
            )}
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={onClose}
                className="cursor-pointer rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover"
              >
                Cerrar
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-fg-muted">
              Este usuario está en BD pero no tiene cuenta Keycloak. Esta acción genera password temporal, crea la
              cuenta en Keycloak con el rol elegido y vincula el subject al registro existente. Si el correo ya está
              en Keycloak por otra vía, sólo se vincula.
            </p>
            <label className="block">
              <span className="text-xs uppercase text-fg-subtle">Rol *</span>
              <select
                value={rol}
                onChange={(e) => setRol(e.target.value)}
                className="mt-1 w-full rounded border px-2 py-1.5"
              >
                <option value="">— elegir rol —</option>
                {ROLES_INTERNOS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase text-fg-subtle">Motivo</span>
              <textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                rows={3}
                className="mt-1 w-full rounded border px-2 py-1.5"
                placeholder="Ej. aprovisionar para que pueda operar Telecom…"
              />
            </label>
            {mutation.error && (
              <p className="rounded border border-estado-err/25 bg-estado-err-bg p-2 text-xs text-estado-err">
                {errorMessage(mutation.error)}
              </p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="cursor-pointer rounded border px-3 py-1.5 text-sm transition hover:bg-surface-2">
                Cancelar
              </button>
              <button
                type="button"
                disabled={!rol || !motivo.trim() || mutation.isPending}
                onClick={() => mutation.mutate()}
                className="cursor-pointer rounded bg-primary px-3 py-1.5 text-sm text-white transition hover:bg-primary-hover disabled:opacity-50"
              >
                {mutation.isPending ? "Aprovisionando…" : "Aprovisionar"}
              </button>
            </div>
          </>
        )}
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-surface shadow-overlay">
        <header className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="text-sm font-semibold text-fg">{title}</h2>
          <button type="button" onClick={onClose} className="cursor-pointer text-fg-faint transition hover:text-fg-muted">
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extrae el mejor mensaje legible de un error de axios. Prioriza
 * {@code response.data.detail} (RFC 7807 ProblemDetail emitido por el backend),
 * después {@code response.data.message}, y cae al {@code error.message} genérico.
 */
function errorMessage(err: unknown): string {
  if (err && typeof err === "object" && "response" in err) {
    const resp = (err as { response?: { data?: { detail?: string; message?: string } } }).response;
    if (resp?.data?.detail) return resp.data.detail;
    if (resp?.data?.message) return resp.data.message;
  }
  if (err instanceof Error) return err.message;
  return "Error desconocido";
}

function toneEstatus(estatus: string): "ok" | "info" | "warn" | "neutral" {
  if (estatus === "ACTIVO") return "ok";
  if (estatus === "SUSPENDIDO" || estatus === "BAJA") return "warn";
  if (estatus === "PENDIENTE_ACTIVACION") return "neutral";
  return "info";
}

function Badge({ tone, children }: { tone: "ok" | "info" | "warn" | "neutral"; children: React.ReactNode }) {
  const cls = {
    ok: "bg-estado-ok-bg text-estado-ok",
    info: "bg-estado-info-bg text-estado-info",
    warn: "bg-estado-warn-bg text-estado-warn",
    neutral: "bg-estado-block-bg text-estado-block",
  }[tone];
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{children}</span>
  );
}

function formatoFecha(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("es-MX", { year: "numeric", month: "2-digit", day: "2-digit" });
  } catch {
    return iso;
  }
}
